import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { contactListApi, turfMapApi } from '../lib/api'
import type { SaveContactListRequest, FilterEvaluationRequest } from '../types/contactList'

export function useContactLists() {
  return useQuery({
    queryKey: ['lists'],
    queryFn: async () => {
      const response = await contactListApi.getAll()
      return response.data
    },
  })
}

export function useContactListDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['lists', id],
    queryFn: async () => {
      if (!id) throw new Error('List ID is required')
      const response = await contactListApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateContactList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveContactListRequest) =>
      contactListApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

export function useUpdateContactList(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveContactListRequest) =>
      contactListApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['lists', id], data)
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

export function useDeleteContactList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => contactListApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

export function useFilterCount(request: FilterEvaluationRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['lists', 'count', request],
    queryFn: async () => {
      if (!request) throw new Error('Request is required')
      const response = await contactListApi.getCount(request)
      return response.data
    },
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000, // 10s - counts change rarely mid-session
  })
}

export function useFilterPreview(request: FilterEvaluationRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['lists', 'preview', request],
    queryFn: async () => {
      if (!request) throw new Error('Request is required')
      const response = await contactListApi.getPreview(request)
      return response.data
    },
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
  })
}

export function useMaterializeList() {
  // Enqueues the materialize job and resolves with { jobId } at the 202-accept — the list isn't
  // actually re-materialized yet. No onSuccess invalidation here: the definitive ['lists']
  // refresh happens once the caller's useJobPolling(jobId) reaches Succeeded (see ListsPage),
  // matching the AutoGenerateModal/TurfCutterPage precedent (invalidate on completion, not enqueue).
  return useMutation({
    mutationFn: (id: string) => contactListApi.materialize(id).then(r => r.data),
  })
}

export function useFilterFieldMetadata() {
  return useQuery({
    queryKey: ['lists', 'field-metadata'],
    queryFn: async () => {
      const response = await contactListApi.getFieldMetadata()
      return response.data
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - field metadata rarely changes
  })
}

export function useAvailableTags() {
  return useQuery({
    queryKey: ['lists', 'tags'],
    queryFn: async () => {
      const response = await contactListApi.getTags()
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useContactsWithEmployment(listId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['lists', listId, 'contacts-with-employment'],
    queryFn: async () => {
      if (!listId) throw new Error('List ID is required')
      const response = await contactListApi.getContactsWithEmployment(listId)
      return response.data
    },
    enabled: enabled && !!listId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useTurfMapContactsWithEmployment(turfMapId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['turf-maps', turfMapId, 'contacts-with-employment'],
    queryFn: async () => {
      if (!turfMapId) throw new Error('TurfMap ID is required')
      const response = await turfMapApi.getContactsWithEmployment(turfMapId)
      return response.data
    },
    enabled: enabled && !!turfMapId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useListOverview(id: string | undefined) {
  return useQuery({
    queryKey: ['lists', id, 'overview'],
    queryFn: async () => {
      if (!id) throw new Error('List ID is required')
      const response = await contactListApi.getOverview(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useUpdateListNotes(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notes: string) => contactListApi.updateNotes(id, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists', id, 'overview'] })
    },
  })
}
